import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, MessageSquare, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { getOrCreateDirectConversationByUsername } from "@/lib/messaging";

export const Route = createFileRoute("/messages/")({
  component: MessagesPage,
  head: () => ({ meta: [{ title: "Xamseen — Messages" }] }),
});

type Conv = {
  id: string;
  title: string | null;
  conv_type: string;
  last_message_at: string;
  members: { user_id: string; profiles: { username: string; full_name: string | null; avatar_url: string | null } | null }[];
  last?: { body: string; sender_id: string } | null;
};

function MessagesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [convs, setConvs] = useState<Conv[]>([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const { data: memberships } = await supabase
      .from("conversation_members")
      .select("conversation_id")
      .eq("user_id", user.id);
    const ids = (memberships ?? []).map((m: any) => m.conversation_id);
    if (!ids.length) { setConvs([]); setLoading(false); return; }
    const { data } = await supabase
      .from("conversations")
      .select("id, title, conv_type, last_message_at")
      .in("id", ids)
      .order("last_message_at", { ascending: false });

    const enriched: Conv[] = [];
    for (const c of data ?? []) {
      const { data: members } = await supabase
        .from("conversation_members")
        .select("user_id, profiles:profiles!conversation_members_user_id_fkey(username, full_name, avatar_url)")
        .eq("conversation_id", c.id);
      // fallback without explicit fk
      let memberRows = members as any;
      if (!members) {
        const { data: m2 } = await supabase.from("conversation_members").select("user_id").eq("conversation_id", c.id);
        memberRows = m2;
      }
      const { data: last } = await supabase
        .from("messages")
        .select("body, sender_id")
        .eq("conversation_id", c.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      enriched.push({ ...c, members: memberRows ?? [], last: last as any });
    }
    setConvs(enriched);
    setLoading(false);
  };

  useEffect(() => { load(); }, [user?.id]);

  const startConversation = async (username: string) => {
    if (!user) return;
    try {
      const id = await getOrCreateDirectConversationByUsername(user.id, username);
      navigate({ to: "/messages/$id", params: { id } });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not start this conversation.");
    }
  };

  if (!user) return <AppShell><SignedOut /></AppShell>;

  return (
    <AppShell>
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Messages</h1>
            <p className="mt-1 text-sm text-muted-foreground">Private messages with neighbors, project teams and community moderators.</p>
          </div>
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-2"><Plus className="h-4 w-4" />New</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Start a conversation</DialogTitle></DialogHeader>
              <NewConvForm onStart={(u) => { setOpenNew(false); startConversation(u); }} />
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="py-20 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : convs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center">
            <MessageSquare className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="font-display font-medium">No conversations yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Start a private chat with another community member.</p>
          </div>
        ) : (
          <div className="divide-y divide-border rounded-xl border border-border bg-card">
            {convs.map((c) => {
              const other = c.members.find((m) => m.user_id !== user.id);
              const name = c.title || other?.profiles?.full_name || other?.profiles?.username || "Conversation";
              return (
                <Link key={c.id} to="/messages/$id" params={{ id: c.id }}
                  className="flex items-center gap-3 p-4 hover:bg-accent">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={other?.profiles?.avatar_url ?? undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground">{(name[0] ?? "C").toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-medium">{name}</span>
                      <span className="shrink-0 text-xs text-muted-foreground">{formatDistanceToNow(new Date(c.last_message_at), { addSuffix: true })}</span>
                    </div>
                    <p className="truncate text-sm text-muted-foreground">{c.last?.body ?? "No messages yet"}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function NewConvForm({ onStart }: { onStart: (username: string) => void }) {
  const [u, setU] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (u.trim()) onStart(u); }} className="space-y-3">
      <label className="text-sm font-medium">Recipient username</label>
      <Input value={u} onChange={(e) => setU(e.target.value)} placeholder="username" autoFocus />
      <Button type="submit" className="w-full">Start conversation</Button>
    </form>
  );
}

function SignedOut() {
  return (
    <div className="mx-auto max-w-md py-20 text-center">
      <MessageSquare className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
      <h1 className="font-display text-xl font-semibold">Sign in to use messages</h1>
      <Button asChild className="mt-4"><Link to="/auth">Sign in</Link></Button>
    </div>
  );
}
